import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Col, DatePicker, Divider, Form, Input, Layout, Popconfirm, Row, Space, Switch, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import apis from '../../apis';
import { useAuth } from '../../context/DataContext';

const { Content } = Layout;
const { Title, Text } = Typography;

const AWARD_TIERS = ['Sapphire', 'Diamond', 'Gold', 'Silver', 'Public'];

const SystemSettings = () => {
    const { currentEventId, setCurrentEventId } = useAuth();
    const [form] = Form.useForm();
    const [eligibilityForm] = Form.useForm();
    const [schedule, setSchedule] = useState([]);
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        form.setFieldsValue({ currentEventId });
        fetchSettings();
    }, [currentEventId, form]);

    const fetchSettings = async () => {
        try {
            const res = await apis.systemSettings.getGlobalSettings();
            if (res.data?.ticketEligibility) {
                const { enabled, schedule: s } = res.data.ticketEligibility;
                setIsEnabled(enabled || false);
                setSchedule(s || []);
            }
        } catch (error) {
            console.error("Error fetching settings:", error);
        }
    };

    const onFinish = async (values) => {
        try {
            const newEventId = values.currentEventId.trim();
            if (!newEventId) {
                message.error('Event ID cannot be empty');
                return;
            }

            const res = await apis.systemSettings.updateGlobalSettings({ currentEventId: newEventId });
            if (res.error) throw new Error(res.error);

            setCurrentEventId(newEventId);
            message.success(`Successfully updated global event ID to ${newEventId}`);
        } catch (error) {
            console.error("Error updating global settings:", error);
            message.error("Failed to update global event ID.");
        }
    };

    const handleSaveEligibility = async (newIsEnabled = isEnabled, newSchedule = schedule) => {
        setLoading(true);
        try {
            const payload = {
                ticketEligibility: {
                    enabled: newIsEnabled,
                    schedule: newSchedule
                }
            };
            const res = await apis.systemSettings.updateGlobalSettings(payload);
            if (res.error) throw new Error(res.error);
            
            setIsEnabled(newIsEnabled);
            setSchedule(newSchedule);
            message.success('Ticket eligibility settings updated');
        } catch (error) {
            console.error("Error updating eligibility:", error);
            message.error("Failed to update ticket eligibility settings.");
        } finally {
            setLoading(false);
        }
    };

    const addScheduleEntry = (values) => {
        const dateStr = values.date.format('YYYY-MM-DD');
        if (schedule.some(item => item.date === dateStr)) {
            message.error('An entry for this date already exists');
            return;
        }
        const newSchedule = [...schedule, { date: dateStr, allowedTiers: values.tiers }];
        newSchedule.sort((a, b) => a.date.localeCompare(b.date));
        handleSaveEligibility(isEnabled, newSchedule);
        eligibilityForm.resetFields();
    };

    const removeScheduleEntry = (date) => {
        const newSchedule = schedule.filter(item => item.date !== date);
        handleSaveEligibility(isEnabled, newSchedule);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => dayjs(text).format('DD MMM YYYY'),
            sorter: (a, b) => a.date.localeCompare(b.date),
        },
        {
            title: 'Allowed Tiers',
            dataIndex: 'allowedTiers',
            key: 'allowedTiers',
            render: (tiers) => (
                <>
                    {(tiers || []).map(tier => {
                        let color = 'default';
                        if (tier === 'Sapphire') color = 'purple';
                        if (tier === 'Diamond') color = 'cyan';
                        if (tier === 'Gold') color = 'gold';
                        if (tier === 'Silver') color = 'geekblue';
                        if (tier === 'Public') color = 'green';
                        return <Tag color={color} key={tier}>{tier}</Tag>;
                    })}
                </>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Popconfirm title="Remove this entry?" onConfirm={() => removeScheduleEntry(record.date)}>
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            <Title level={2}>System Settings</Title>
            <Text type="secondary">Manage global application configurations here. Changes apply immediately across the entire platform.</Text>

            <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
                <Col span={10}>
                    <Card title="Event Configuration" bordered={false} style={{ height: '100%' }}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                        >
                            <Form.Item
                                label="Current Event ID"
                                name="currentEventId"
                                rules={[{ required: true, message: 'Please input the active event ID!' }]}
                                extra="This determines which event registrants will be added to, and which seat collections will be queried."
                            >
                                <Input placeholder="e.g. APCS2026" />
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit">
                                    Save Changes
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                <Col span={14}>
                    <Card 
                        title="Ticket Eligibility Schedule" 
                        bordered={false}
                        extra={
                            <Space>
                                <Text>Enabled</Text>
                                <Switch checked={isEnabled} onChange={(val) => handleSaveEligibility(val, schedule)} loading={loading} />
                            </Space>
                        }
                    >
                        <Text type="secondary" style={{ display: 'block', marginBottom: '20px' }}>
                            Define a window for specific award tiers to purchase tickets. If disabled, all winners can buy anytime.
                        </Text>

                        <Form form={eligibilityForm} layout="inline" onFinish={addScheduleEntry} style={{ marginBottom: '24px' }}>
                            <Form.Item name="date" rules={[{ required: true, message: 'Pick date' }]}>
                                <DatePicker placeholder="Select Date" />
                            </Form.Item>
                            <Form.Item name="tiers" rules={[{ required: true, message: 'Select at least one' }]}>
                                <Checkbox.Group options={AWARD_TIERS} />
                            </Form.Item>
                            <Form.Item>
                                <Button type="dashed" htmlType="submit" icon={<PlusOutlined />}>
                                    Add Entry
                                </Button>
                            </Form.Item>
                        </Form>

                        <Table 
                            dataSource={schedule} 
                            columns={columns} 
                            rowKey="date" 
                            pagination={{ pageSize: 5 }} 
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </Content>
    );
};

export default SystemSettings;
